import { useAppScopeStore } from '@frontend/scope';
import {
  useListAppointmentTypesInfiniteQuery,
  useListAppointmentTypesMultiInfiniteQuery,
  useListAppointmentTypesMultiQuery,
  useListAppointmentTypesQuery,
  useListProvidersInfiniteQuery,
  useListProvidersMultiInfiniteQuery,
} from '../queries';
import { PageConfig } from '../types';
import { useGetSchedulerV3FeatureFlagDetails } from './use-get-scheduler-v3-feature-flag-details';

type UseGetAppointmentTypesV3DataParamsType = {
  selectedLocationId: string;
  selectedLocationIds?: string[];
  debouncedSearchValue?: string;
  pageConfig?: PageConfig;
  showHiddenOnCalendar?: boolean;
  isEnabled?: boolean;
};

export const useGetAppointmentTypesV3Data = ({
  selectedLocationId,
  debouncedSearchValue,
  selectedLocationIds,
  pageConfig,
  showHiddenOnCalendar = true,
  isEnabled = true,
}: UseGetAppointmentTypesV3DataParamsType) => {
  const { isScheduleV3FlagEnabled } = useGetSchedulerV3FeatureFlagDetails();
  const { selectedGroupId, isSingleTypeScope } = useAppScopeStore();
  const parentLocationId = selectedGroupId;
  const isMultiLocation = !isSingleTypeScope;
  const isMultiLocationApiCall = !!parentLocationId && isMultiLocation;

  const listAppointmentTypesQuery = useListAppointmentTypesQuery({
    request: {
      ...(pageConfig?.limit && { limit: pageConfig?.limit }),
      ...(pageConfig?.page && { page: pageConfig?.page }),
      ...(debouncedSearchValue && { name: debouncedSearchValue }),
      ...(showHiddenOnCalendar && { showHidden: showHiddenOnCalendar }),
      locationId: selectedLocationId,
    },
    options: {
      enabled: !isMultiLocationApiCall && isEnabled && isScheduleV3FlagEnabled && !!selectedLocationId,
    },
  });

  const listAppointmentTypesMultiQuery = useListAppointmentTypesMultiQuery({
    request: {
      ...(pageConfig?.limit && { limit: pageConfig?.limit }),
      ...(pageConfig?.page && { page: pageConfig?.page }),
      ...(debouncedSearchValue && { name: debouncedSearchValue }),
      ...(showHiddenOnCalendar && { showHidden: showHiddenOnCalendar }),
      locationsIds: (selectedLocationIds || []).sort(),
      parentLocationId: parentLocationId || '',
    },
    options: {
      enabled: isMultiLocationApiCall && !!selectedLocationIds?.length && isEnabled && isScheduleV3FlagEnabled,
    },
  });

  return isMultiLocationApiCall ? listAppointmentTypesMultiQuery : listAppointmentTypesQuery;
};

export const useGetAppointmentTypeInfiniteListForV3 = ({
  locationId,
  pageLimit = 50,
  searchText,
}: {
  locationId: string;
  pageLimit?: number;
  searchText?: string;
}) => {
  const { isScheduleV3FlagEnabled } = useGetSchedulerV3FeatureFlagDetails();
  const { selectedGroupId, isSingleTypeScope } = useAppScopeStore();
  const parentLocationId = selectedGroupId;
  const isMultiLocation = !isSingleTypeScope;

  const singleLocationAppointmentTypeQuery = useListAppointmentTypesInfiniteQuery({
    request: {
      locationId: locationId,
      limit: pageLimit,
      name: searchText,
      showHidden: true,
    },
    options: {
      enabled: !isMultiLocation && !!locationId && isScheduleV3FlagEnabled,
      select: (data) => {
        return {
          ...data,
          pages: data.pages.map((page, index) => ({
            rows: page.appointmentTypes ?? [],
            totalCount: page.totalCount ?? 0,
            page: index + 1,
            totalPages: Math.ceil((page.totalCount ?? 0) / pageLimit),
          })),
        };
      },
    },
  });

  const multiLocationAppointmentTypeQuery = useListAppointmentTypesMultiInfiniteQuery({
    request: {
      locationsIds: [locationId],
      parentLocationId: parentLocationId,
      limit: pageLimit,
      name: searchText,
      showHidden: true,
    },
    options: {
      enabled: isMultiLocation && !!locationId && isScheduleV3FlagEnabled,
      select: (data) => {
        return {
          ...data,
          pages: data.pages.map((page, index) => ({
            rows: page.appointmentTypes ?? [],
            totalCount: page.totalCount ?? 0,
            page: index + 1,
            totalPages: Math.ceil((page.totalCount ?? 0) / pageLimit),
          })),
        };
      },
    },
  });

  return isMultiLocation ? multiLocationAppointmentTypeQuery : singleLocationAppointmentTypeQuery;
};

export const useGetPractitionerInfiniteListForV3 = ({
  locationId,
  pageLimit = 50,
  searchText,
}: {
  locationId: string;
  pageLimit?: number;
  searchText?: string;
}) => {
  const { isScheduleV3FlagEnabled } = useGetSchedulerV3FeatureFlagDetails();
  const { selectedGroupId, isSingleTypeScope } = useAppScopeStore();
  const parentLocationId = selectedGroupId;
  const isMultiLocation = !isSingleTypeScope;

  const singleLocationProvidersQuery = useListProvidersInfiniteQuery({
    request: {
      locationId: locationId,
      limit: pageLimit,
      name: searchText,
      showHidden: true,
    },
    options: {
      enabled: !isMultiLocation && !!locationId && isScheduleV3FlagEnabled,
      select: (data) => {
        return {
          ...data,
          pages: data.pages.map((page) => ({
            totalCount: page.totalCount,
            rows: page.providers,
          })),
        };
      },
    },
  });
  const multiLocationProvidersQuery = useListProvidersMultiInfiniteQuery({
    request: {
      locationIds: [locationId],
      parentLocationId: parentLocationId,
      limit: pageLimit,
      name: searchText,
      showHidden: true,
    },
    options: {
      enabled: isMultiLocation && !!locationId && !!parentLocationId && isScheduleV3FlagEnabled,
      select: (data) => {
        return {
          ...data,
          pages: data.pages.map((page) => ({
            totalCount: page.totalCount,
            rows: page.providers,
          })),
        };
      },
    },
  });

  return isMultiLocation ? multiLocationProvidersQuery : singleLocationProvidersQuery;
};
